import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";

import {
  createConsultantProposal,
  createConsultantProposalFromCv,
  listPartnerLinks,
} from "../../../api/partnersApi";
import { listConsultants } from "../../../api/consultantsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

const SUPPORTED_EXTS = [".pdf", ".docx"];

const ConsultantProposalFormModal = ({ isOpen, onClose, onSuccess }) => {
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { selectedBusinessUnitId, features } = useBusinessUnit();
  const hasPilot = Boolean(features?.has_pilot);

  const [mode, setMode] = useState(hasPilot ? "pilot" : "cv");
  const [consultantId, setConsultantId] = useState("");
  const [cvFile, setCvFile] = useState(null);
  const [dailyRate, setDailyRate] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [availabilityDate, setAvailabilityDate] = useState("");
  const [recipientIds, setRecipientIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const [consultants, setConsultants] = useState([]);
  const [loadingConsultants, setLoadingConsultants] = useState(false);
  const [partnerLinks, setPartnerLinks] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);

  const resetForm = useCallback(() => {
    setMode(hasPilot ? "pilot" : "cv");
    setConsultantId("");
    setCvFile(null);
    setDailyRate("");
    setCurrency("EUR");
    setAvailabilityDate("");
    setRecipientIds([]);
    setDragActive(false);
  }, [hasPilot]);

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingPartners(true);
    listPartnerLinks({}, { buId: selectedBusinessUnitId || undefined })
      .then((res) => setPartnerLinks(res?.items || []))
      .catch(() =>
        toast({
          title: t(
            "partners.consultantProposals.errors.loadPartners",
            "Failed to load partner companies"
          ),
          status: "error",
        })
      )
      .finally(() => setLoadingPartners(false));
  }, [isOpen, selectedBusinessUnitId, toast, t]);

  useEffect(() => {
    if (!isOpen || mode !== "pilot" || !hasPilot) return;
    setLoadingConsultants(true);
    listConsultants(
      { page: 1, pageSize: 100 },
      { buId: selectedBusinessUnitId || undefined }
    )
      .then((res) => setConsultants(res?.items || []))
      .catch(() =>
        toast({
          title: t(
            "partners.consultantProposals.errors.loadConsultants",
            "Failed to load consultants"
          ),
          status: "error",
        })
      )
      .finally(() => setLoadingConsultants(false));
  }, [isOpen, mode, hasPilot, selectedBusinessUnitId, toast, t]);

  const validRecipientOptions = useMemo(
    () =>
      partnerLinks.map((l) => ({
        value: String(l.partnerCompanyId),
        label:
          l.partnerCompanyName ||
          l.partnerName ||
          `${t("partners.consultantProposals.partnerNumber", "Partner")} #${l.partnerCompanyId}`,
      })),
    [partnerLinks, t]
  );

  const acceptFile = (file) => {
    if (!file) return;
    const ext = file.name.includes(".")
      ? `.${file.name.split(".").pop().toLowerCase()}`
      : "";
    if (!SUPPORTED_EXTS.includes(ext)) {
      toast({
        title: t(
          "partners.consultantProposals.errors.unsupportedFile",
          "Only PDF and DOCX files are supported"
        ),
        status: "error",
      });
      return;
    }
    setCvFile(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    acceptFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      acceptFile(e.dataTransfer.files[0]);
    }
  };

  const validate = () => {
    if (!recipientIds.length) {
      toast({
        title: t(
          "partners.consultantProposals.errors.noRecipients",
          "Select at least one partner company"
        ),
        status: "warning",
      });
      return false;
    }
    if (mode === "pilot" && !consultantId) {
      toast({
        title: t(
          "partners.consultantProposals.errors.noConsultant",
          "Pick a consultant from your pool"
        ),
        status: "warning",
      });
      return false;
    }
    if (mode === "cv" && !cvFile) {
      toast({
        title: t(
          "partners.consultantProposals.errors.noCv",
          "Upload a CV file"
        ),
        status: "warning",
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const recipientPartnerCompanyIds = recipientIds.map((id) => Number(id));
      if (mode === "pilot") {
        await createConsultantProposal(
          {
            consultantId: Number(consultantId),
            recipientPartnerCompanyIds,
            dailyRate: dailyRate ? Number(dailyRate) : null,
            currency,
            availabilityDate: availabilityDate || null,
          },
          { buId: selectedBusinessUnitId || undefined }
        );
      } else {
        const formData = new FormData();
        formData.append("cvFile", cvFile);
        formData.append(
          "recipientPartnerCompanyIds",
          JSON.stringify(recipientPartnerCompanyIds)
        );
        if (dailyRate) formData.append("dailyRate", String(Number(dailyRate)));
        formData.append("currency", currency);
        if (availabilityDate)
          formData.append("availabilityDate", availabilityDate);
        await createConsultantProposalFromCv(formData, {
          buId: selectedBusinessUnitId || undefined,
        });
      }
      toast({
        title: t(
          "partners.consultantProposals.toasts.created",
          "Consultant proposal sent"
        ),
        status: "success",
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: t(
          "partners.consultantProposals.errors.createFailed",
          "Failed to send proposal"
        ),
        description: detail || err?.message,
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {t(
            "partners.consultantProposals.form.title",
            "Propose a Consultant"
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="info" variant="left-accent">
              <AlertIcon />
              <Text fontSize="sm">
                {t(
                  "partners.consultantProposals.form.anonymisedNotice",
                  "The receiving partner will see only the anonymised profile (no name, email, or phone) plus your daily rate and availability."
                )}
              </Text>
            </Alert>

            <FormControl>
              <FormLabel>
                {t("partners.consultantProposals.form.source", "Source")}
              </FormLabel>
              <RadioGroup value={mode} onChange={setMode}>
                <Stack direction="row" spacing={6}>
                  <Radio value="pilot" isDisabled={!hasPilot}>
                    {t(
                      "partners.consultantProposals.form.sourcePilot",
                      "Pick from Pilot"
                    )}
                  </Radio>
                  <Radio value="cv">
                    {t(
                      "partners.consultantProposals.form.sourceCv",
                      "Upload CV"
                    )}
                  </Radio>
                </Stack>
              </RadioGroup>
              {!hasPilot && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t(
                    "partners.consultantProposals.form.pilotDisabled",
                    "Pilot mode is unavailable — your business unit doesn't have ASAP Pilot enabled."
                  )}
                </Text>
              )}
            </FormControl>

            {mode === "pilot" && (
              <FormControl isRequired>
                <FormLabel>
                  {t(
                    "partners.consultantProposals.form.consultant",
                    "Consultant"
                  )}
                </FormLabel>
                {loadingConsultants ? (
                  <Spinner size="sm" />
                ) : (
                  <Select
                    placeholder={t(
                      "partners.consultantProposals.form.selectConsultant",
                      "Select a consultant"
                    )}
                    value={consultantId}
                    onChange={(e) => setConsultantId(e.target.value)}
                  >
                    {consultants.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                        {c.title ? ` — ${c.title}` : ""}
                      </option>
                    ))}
                  </Select>
                )}
              </FormControl>
            )}

            {mode === "cv" && (
              <FormControl isRequired>
                <FormLabel>
                  {t(
                    "partners.consultantProposals.form.cvFile",
                    "CV file (PDF or DOCX)"
                  )}
                </FormLabel>
                <Box
                  border="2px dashed"
                  borderColor={dragActive ? "blue.400" : "gray.300"}
                  borderRadius="md"
                  p={6}
                  minH="120px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg={dragActive ? "blue.50" : "gray.50"}
                  transition="all 0.2s"
                  cursor="pointer"
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  _hover={{ bg: "gray.100" }}
                >
                  <VStack spacing={2}>
                    <Icon as={FaCloudUploadAlt} boxSize={8} color="gray.400" />
                    <Text
                      fontSize="sm"
                      color="gray.600"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {t(
                        "partners.consultantProposals.form.cvDropzone",
                        "Drag & drop a CV here, or click to browse"
                      )}
                    </Text>
                    <Text
                      fontSize="xs"
                      color="gray.400"
                      sx={{ marginBottom: "0 !important" }}
                    >
                      {t(
                        "partners.consultantProposals.form.cvAccepted",
                        "PDF or DOCX, one file"
                      )}
                    </Text>
                  </VStack>
                </Box>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={handleFileChange}
                />
                {cvFile && (
                  <Flex
                    align="center"
                    justify="space-between"
                    bg="gray.50"
                    px={3}
                    py={1}
                    borderRadius="md"
                    mt={2}
                  >
                    <Text fontSize="sm" isTruncated maxW="80%">
                      {cvFile.name}
                    </Text>
                    <IconButton
                      aria-label="remove"
                      icon={<FaTimes />}
                      size="xs"
                      variant="ghost"
                      onClick={() => setCvFile(null)}
                    />
                  </Flex>
                )}
              </FormControl>
            )}

            <HStack spacing={3}>
              <FormControl>
                <FormLabel>
                  {t(
                    "partners.consultantProposals.form.dailyRate",
                    "Daily rate"
                  )}
                </FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={dailyRate}
                  onChange={(e) => setDailyRate(e.target.value)}
                />
              </FormControl>
              <FormControl maxW="120px">
                <FormLabel>
                  {t(
                    "partners.consultantProposals.form.currency",
                    "Currency"
                  )}
                </FormLabel>
                <Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>
                {t(
                  "partners.consultantProposals.form.availabilityDate",
                  "Availability date"
                )}
              </FormLabel>
              <Input
                type="date"
                value={availabilityDate}
                onChange={(e) => setAvailabilityDate(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>
                {t(
                  "partners.consultantProposals.form.recipients",
                  "Send to partner companies"
                )}
              </FormLabel>
              {loadingPartners ? (
                <Spinner size="sm" />
              ) : validRecipientOptions.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  {t(
                    "partners.consultantProposals.form.noPartners",
                    "No active partner links yet — add a partner first."
                  )}
                </Text>
              ) : (
                <CheckboxGroup
                  value={recipientIds}
                  onChange={(values) => setRecipientIds(values)}
                >
                  <Stack direction="column" spacing={1} maxH="180px" overflowY="auto">
                    {validRecipientOptions.map((opt) => (
                      <Checkbox key={opt.value} value={opt.value}>
                        {opt.label}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              )}
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" onClick={onClose} isDisabled={submitting}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            isLoading={submitting}
          >
            {t("partners.consultantProposals.form.submit", "Send proposal")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConsultantProposalFormModal;
